import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../auth/registered/SEC_00_DB_Payload";

const BASE = "/api/v1/sustina/company-chart-of-accounts";

/**
 * 🛰️ LIST CHART OF ACCOUNTS
 * The persistent, per-company, user-managed dataset of GL/cost codes.
 * Targets: CompanyChartOfAccountsController.list (Corp.usp_Z_14_CompanyChartOfAccounts_List)
 */
export const fetchChartOfAccounts = async (corporateEntityId, config) => {
    try {
        const rawData = await dbPayload(`${BASE}/list`, "POST", config.siteKey,
            GoldenSourceHelper.prepareForApi({ CorporateEntityId: corporateEntityId }));
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Chart Of Accounts List Failed:", err);
        return [];
    }
};

/**
 * 🛰️ ADD / UPDATE / REMOVE a single dataset row
 * Targets: CompanyChartOfAccountsController.[add|update|remove]
 */
export const addChartOfAccountsRow = async (row, config) => {
    const rawData = await dbPayload(`${BASE}/add`, "POST", config.siteKey, GoldenSourceHelper.prepareForApi(row));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Could not add row.");
    return res.data;
};

export const updateChartOfAccountsRow = async (row, config) => {
    const rawData = await dbPayload(`${BASE}/update`, "POST", config.siteKey, GoldenSourceHelper.prepareForApi(row));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Could not update row.");
    return res.data;
};

export const removeChartOfAccountsRow = async (chartOfAccountsRowId, config) => {
    const rawData = await dbPayload(`${BASE}/remove`, "POST", config.siteKey,
        GoldenSourceHelper.prepareForApi({ ChartOfAccountsRowId: chartOfAccountsRowId }));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Could not remove row.");
    return true;
};

/**
 * 🛰️ BULK IMPORT
 * Parsed client-side (CSV/GL/XBRL export - no S3, no file upload). Every
 * row lands in the dataset regardless of outcome; mapped only when its
 * XbrlTag resolves or SourceCode matches a prior mapping.
 * Targets: CompanyChartOfAccountsController.bulk-import (Corp.usp_Z_14_CompanyChartOfAccounts_BulkImport)
 */
export const bulkImportChartOfAccounts = async (corporateEntityId, rows, config) => {
    const rawData = await dbPayload(`${BASE}/bulk-import`, "POST", config.siteKey,
        GoldenSourceHelper.prepareForApi({ CorporateEntityId: corporateEntityId, Rows: rows }));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Bulk import failed.");
    return res.data || [];
};

/**
 * 🛰️ SET / CLEAR MAPPING
 * Manually link (or unlink) a dataset row to a taxonomy scope - either an
 * existing AttributeDefinitionId or a NewAttributeLabel to create one.
 * Targets: CompanyChartOfAccountsController.[set-mapping|clear-mapping]
 */
export const setChartOfAccountsMapping = async (mapping, config) => {
    const rawData = await dbPayload(`${BASE}/set-mapping`, "POST", config.siteKey, GoldenSourceHelper.prepareForApi(mapping));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Could not save mapping.");
    return res.data;
};

export const clearChartOfAccountsMapping = async (chartOfAccountsRowId, config) => {
    const rawData = await dbPayload(`${BASE}/clear-mapping`, "POST", config.siteKey,
        GoldenSourceHelper.prepareForApi({ ChartOfAccountsRowId: chartOfAccountsRowId }));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Could not clear mapping.");
    return true;
};

/**
 * 🛰️ PUBLISH
 * Snapshots every mapped row into an immutable version - that snapshot,
 * not the live dataset, is what a declaration uses.
 * Targets: CompanyChartOfAccountsController.publish (Corp.usp_Z_15_CompanyChartOfAccounts_Publish)
 */
export const publishChartOfAccounts = async (corporateEntityId, config) => {
    const rawData = await dbPayload(`${BASE}/publish`, "POST", config.siteKey,
        GoldenSourceHelper.prepareForApi({ CorporateEntityId: corporateEntityId }));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res.success) throw new Error(res.message || "Publish failed.");
    return res.data;
};

export const fetchPublishedVersions = async (corporateEntityId, config) => {
    try {
        const rawData = await dbPayload(`${BASE}/published-version/list`, "POST", config.siteKey,
            GoldenSourceHelper.prepareForApi({ CorporateEntityId: corporateEntityId }));
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Published Version List Failed:", err);
        return [];
    }
};

export const fetchPublishedRows = async (versionId, config) => {
    try {
        const rawData = await dbPayload(`${BASE}/published-version/rows`, "POST", config.siteKey,
            GoldenSourceHelper.prepareForApi({ VersionId: versionId }));
        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Published Rows Fetch Failed:", err);
        return [];
    }
};
